import type { FaqItem } from '../components/SeoHead';

type PageSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

type FeatureItem = {
  title: string;
  description: string;
};

type InternalLinkItem = {
  label: string;
  to: string;
};

export type SeoLandingPageContent = {
  slug: string;
  title: string;
  description: string;
  h1: string;
  heroSubtitle: string;
  intro: string;
  keyBenefits: string[];
  problemSolution: PageSection;
  featureHighlights: FeatureItem[];
  whyWorkflowEstate: string[];
  deepDive?: PageSection;
  audienceFit?: PageSection;
  faqItems: FaqItem[];
  ctaHeading: string;
  ctaBody: string;
  ctaPrimaryText: string;
  ctaPrimaryTo: string;
  ctaSecondaryText: string;
  ctaSecondaryTo: string;
  internalLinks: InternalLinkItem[];
};

const sharedInternalLinks: InternalLinkItem[] = [
  { label: 'Real Estate CRM Australia', to: '/seo/real-estate-crm-australia' },
  { label: 'Real Estate CRM Sydney', to: '/seo/real-estate-crm-sydney' },
  { label: 'CRM for Real Estate Agencies', to: '/seo/crm-for-real-estate-agencies' },
  { label: 'Open House Management Software', to: '/seo/open-house-management-software' },
  { label: 'Workflow.Estate vs Agentbox', to: '/seo/workflow-estate-vs-agentbox' },
  { label: 'Book a Demo', to: '/contact-sales' },
];

export const seoLandingPages: Record<string, SeoLandingPageContent> = {
  '/real-estate-crm-australia': {
    slug: '/real-estate-crm-australia',
    title: 'Real Estate CRM Australia | In-House Workflow CRM',
    description: 'In-house real estate CRM for Australian agencies. Manage appraisal, open homes, buyer follow-up, sold workflow and key handover in one platform.',
    h1: 'Real Estate CRM Australia with In-House Workflow Control',
    heroSubtitle: 'A practical, office-style CRM built for Australian agencies managing properties from appraisal through to sold and key handover.',
    intro: 'Workflow.Estate is designed for Australian real estate operations where agents and offices need one connected system. It links contact management with property workflows so teams can move from market appraisal to campaign execution, negotiation and handover with clear ownership at each step.',
    keyBenefits: [
      'Manage appraisal-to-sold workflows in one in-house office-style CRM',
      'Track buyer interactions and open home follow-up with clearer daily priorities',
      'Keep listing preparation, campaign execution and handovers aligned',
      'Improve agent productivity by reducing admin duplication and context switching',
    ],
    problemSolution: {
      heading: 'Why Australian agencies need more than a contact database',
      paragraphs: [
        'Many agencies across Australia still run daily operations through disconnected tools: one for contacts, one for tasks, one for campaign activity, and separate manual trackers for handover steps. This slows execution and increases the risk of missed follow-up.',
        'Workflow.Estate combines CRM and office workflow management in one platform. Teams can see buyer history, open home outcomes, listing stage progression and next actions in a single operational view from appraisal to sold and key handover.',
      ],
      bullets: [
        'Lead capture is linked directly to property and campaign context',
        'Buyer tracking includes notes, status and follow-up visibility',
        'Listing workflows stay aligned across agents, admins and support roles',
      ],
    },
    featureHighlights: [
      {
        title: 'End-to-end property lifecycle workflow',
        description: 'Support progression from market appraisal through campaign delivery, negotiation stages, sold outcomes and key handover steps.',
      },
      {
        title: 'Daily agent productivity workflow',
        description: 'Provide structured daily priorities, next actions and reminders so agents can focus on client work rather than manual process management.',
      },
      {
        title: 'Open home and buyer follow-up continuity',
        description: 'Capture attendee and buyer context during open homes and move it directly into consistent follow-up workflows.',
      },
      {
        title: 'Office coordination and visibility',
        description: 'Help principals and administrators monitor workflow progress, unblock bottlenecks and maintain execution consistency across listings.',
      },
    ],
    whyWorkflowEstate: [
      'Purpose-built around Australian agency operations from appraisal to handover',
      'Balances agent productivity with office-level workflow control',
      'Supports clearer execution standards across the full listing lifecycle',
    ],
    deepDive: {
      heading: 'How Workflow.Estate supports productivity in Australian agency teams',
      paragraphs: [
        'A strong real estate CRM in Australia should support operational execution, not just contact storage. Agents need visibility on today’s tasks, open home follow-up, buyer progression and campaign responsibilities, while office teams need confidence that each listing is moving correctly through its stages.',
        'Workflow.Estate addresses this by connecting CRM records with practical workflow operations. Instead of switching between separate systems to understand what happened and what should happen next, teams can work from one source of operational context across appraisal, campaign, negotiation and handover.',
        'The productivity outcome is straightforward: less duplicated admin work, clearer next actions for agents, better handover quality between roles, and more consistent service delivery across listings. For Australian agencies focused on operational efficiency, this office-style model can materially improve day-to-day execution.',
      ],
    },
    audienceFit: {
      heading: 'Best fit for agencies that want structure without complexity',
      paragraphs: [
        'If your team is growing, handling more listings, or tightening follow-up discipline, this model helps standardise execution while keeping daily workflows practical.',
      ],
      bullets: [
        'You want one CRM operating model from appraisal through sold and handover',
        'You need better daily workflow clarity for agents and admins',
        'You are focused on improving productivity without adding process overhead',
      ],
    },
    faqItems: [
      { question: 'Is Workflow.Estate suitable for Australian agencies of different sizes?', answer: 'Yes. It is designed for agencies that need practical CRM and office-style workflow management, from smaller teams to larger multi-agent operations.' },
      { question: 'Can this CRM support open home follow-up?', answer: 'Yes. Open home attendance and buyer follow-up can be captured and managed as part of the same operational workflow.' },
      { question: 'Can it support work from appraisal through to sold and handover?', answer: 'Yes. Workflow.Estate is designed to support property workflow progression from market appraisal through campaign and sold-stage coordination, including handover tasks.' },
      { question: 'Does Workflow.Estate focus only on contacts?', answer: 'No. It combines contact tracking with listing workflow, task coordination and agency operations visibility.' },
    ],
    ctaHeading: 'See how Workflow.Estate fits your agency',
    ctaBody: 'Book a practical walkthrough focused on improving daily agent productivity and office workflow efficiency from appraisal to handover.',
    ctaPrimaryText: 'Book a demo',
    ctaPrimaryTo: '/contact-sales',
    ctaSecondaryText: 'Learn more',
    ctaSecondaryTo: '/demo',
    internalLinks: sharedInternalLinks,
  },
  '/real-estate-crm-sydney': {
    slug: '/real-estate-crm-sydney',
    title: 'Real Estate CRM Sydney | Office-Style Agency CRM',
    description: 'Office-style real estate CRM for Sydney agencies. Run appraisal, open-home follow-up, listing workflow and sold-stage handover with better daily control.',
    h1: 'Real Estate CRM Sydney Agencies Can Run In-House',
    heroSubtitle: 'An office-style CRM for Sydney agencies managing properties from appraisal through to sold and key handover.',
    intro: 'Workflow.Estate helps Sydney agencies run high-activity listing operations in one connected system. From market appraisal and campaign setup to open homes, buyer follow-up and sold-stage coordination, teams can execute with clearer daily ownership.',
    keyBenefits: [
      'Manage appraisal-to-sold workflows with in-house office-style control',
      'Improve daily agent productivity through clear task and follow-up priorities',
      'Maintain consistent buyer engagement across open homes and campaigns',
      'Strengthen handover quality between agents, admins and support roles',
    ],
    problemSolution: {
      heading: 'Sydney agencies need speed and process control together',
      paragraphs: [
        'In Sydney, inspection schedules and enquiry volumes can shift quickly. When teams rely on disconnected systems, daily follow-up can become inconsistent and handover quality can drop.',
        'Workflow.Estate provides a unified CRM and workflow operations view, so agencies can keep appraisal, campaign, open-home and sold-stage tasks visible in one in-house process model.',
      ],
      bullets: [
        'Lead and attendee capture feed directly into practical follow-up workflows',
        'Task progression is structured across each listing stage',
        'Daily priorities are clearer for both agents and office teams',
      ],
    },
    featureHighlights: [
      {
        title: 'Appraisal-to-handover workflow continuity',
        description: 'Support progression from market appraisal through campaign and sold-stage coordination, including handover actions.',
      },
      {
        title: 'Office-style daily execution for agents',
        description: 'Give agents clear daily tasks, reminders and next actions so high-volume weeks remain manageable and consistent.',
      },
      {
        title: 'Open home and buyer follow-up discipline',
        description: 'Convert inspection activity into structured lead follow-up with better consistency across the team.',
      },
      {
        title: 'Shared operational visibility',
        description: 'Enable principals and administrators to monitor workflow health and support faster decisions.',
      },
    ],
    whyWorkflowEstate: [
      'Built for Australian agencies that need in-house workflow control at scale',
      'Connects CRM activity with practical office operations from appraisal to sold',
      'Supports faster response times while preserving execution quality',
    ],
    deepDive: {
      heading: 'How Workflow.Estate improves productivity in Sydney agency teams',
      paragraphs: [
        'Sydney agencies often operate under high campaign intensity, with concentrated open-home schedules and fast buyer follow-up windows. In this context, a contact-only CRM is rarely sufficient. Teams need a workflow model that supports reliable day-to-day execution under pressure.',
        'Workflow.Estate provides that operational structure by connecting buyer activity, listing progression and office tasks in one system. Agents can work from clearer priorities, while administrators and principals can monitor progress and resolve bottlenecks earlier.',
        'The practical benefit is improved consistency and efficiency. Open-home activity can move directly into follow-up workflows, listing stages are easier to manage, and handovers are less dependent on manual tracking. For Sydney teams, this supports stronger daily productivity without sacrificing process quality.',
      ],
    },
    audienceFit: {
      heading: 'A strong fit for Sydney growth teams',
      paragraphs: [
        'Agencies growing listing volume or improving follow-up discipline can use Workflow.Estate to implement practical in-house workflow structure without slowing agents down.',
      ],
      bullets: [
        'You need an in-house CRM workflow from appraisal through sold and key handover',
        'You want clearer daily productivity workflows for agents in fast campaign cycles',
        'You are improving consistency across open-home follow-up and team handover',
      ],
    },
    faqItems: [
      { question: 'Is this only for Sydney?', answer: 'No. Workflow.Estate is built for Australian agencies generally, and this page highlights use cases relevant to Sydney teams.' },
      { question: 'Can we track buyers by property interest?', answer: 'Yes. Buyer activity and follow-up can be managed in relation to specific property campaigns.' },
      { question: 'Does it support agency operations as well as CRM?', answer: 'Yes. Workflow.Estate is designed to connect CRM activity with listing and office workflow execution.' },
      { question: 'Can it support appraisal-to-sold and handover workflows?', answer: 'Yes. Workflow.Estate supports property workflow progression from market appraisal to sold-stage coordination, including handover-related tasks.' },
    ],
    ctaHeading: 'Book a Sydney-focused CRM walkthrough',
    ctaBody: 'See how your Sydney team can improve daily productivity, follow-up quality and in-house workflow coordination from appraisal to handover.',
    ctaPrimaryText: 'Book a demo',
    ctaPrimaryTo: '/contact-sales',
    ctaSecondaryText: 'Watch product demo',
    ctaSecondaryTo: '/demo',
    internalLinks: sharedInternalLinks,
  },
  '/crm-for-real-estate-agencies': {
    slug: '/crm-for-real-estate-agencies',
    title: 'CRM for Real Estate Agencies | Appraisal to Handover',
    description: 'CRM for real estate agencies that connects market appraisal, campaign workflow, open-home leads, buyer follow-up and handover operations.',
    h1: 'CRM for Real Estate Agencies That Need In-House Workflow Control',
    heroSubtitle: 'An in-house office-style CRM for agencies managing properties from market appraisal through to sold and key handover.',
    intro: 'Workflow.Estate is designed for agencies that need one practical operating system across the full property lifecycle. From the first appraisal conversation to campaign delivery, contract progression and key handover, teams can manage work in one structured flow.',
    keyBenefits: [
      'Run appraisal-to-sold workflows in one in-house office-style CRM',
      'Give agents a clear daily task flow across listings, opens and follow-up',
      'Improve handover quality between agents, admins and support staff',
      'Reduce repetitive admin and context switching between disconnected tools',
    ],
    problemSolution: {
      heading: 'Why agencies need an in-house office-style CRM model',
      paragraphs: [
        'Many agencies still run CRM and office operations separately. Contacts may live in one system, while appraisal tasks, campaign preparation, open home activity and handover steps are tracked elsewhere. That split slows teams down and creates avoidable follow-up gaps.',
        'Workflow.Estate brings these layers together. It gives agencies an in-house office-style CRM where daily agent work, property workflow progression and team coordination are managed in one place from market appraisal through to sold and key handover.',
      ],
      bullets: [
        'Property and contact context stay connected at every stage',
        'Daily task priorities are visible for agents and admins',
        'Workflow status is clear from appraisal to final handover',
      ],
    },
    featureHighlights: [
      {
        title: 'Appraisal-to-handover workflow visibility',
        description: 'Manage property progression from market appraisal, campaign and negotiation stages through to sold and key handover actions.',
      },
      {
        title: 'Office-style daily execution',
        description: 'Support daily agent productivity with structured tasks, reminders and next-action clarity across listings and buyers.',
      },
      {
        title: 'Open home and lead follow-up continuity',
        description: 'Capture open home activity and move attendee insights into practical follow-up workflows without losing context.',
      },
      {
        title: 'Team coordination across agency operations',
        description: 'Improve efficiency by aligning agents, administrators and support roles around shared workflow status and responsibilities.',
      },
    ],
    whyWorkflowEstate: [
      'Built around real agency office operations, not just lead pipeline fields',
      'Supports consistent execution from early appraisal through final handover',
      'Helps agents spend more time on client work and less on fragmented admin',
    ],
    deepDive: {
      heading: 'How Workflow.Estate improves daily productivity for agents and offices',
      paragraphs: [
        'For many agencies, productivity issues are not caused by a lack of data. They are caused by fragmented process: one place for contacts, another for campaign tasks, and separate tools for reporting, agreements or follow-up reminders. Agents then spend too much time moving information and checking what has already been done.',
        'Workflow.Estate addresses this with an in-house office-style CRM structure. The same platform can support market appraisal workflows, property campaign progression, open home lead tracking, follow-up execution and final sold-stage coordination. This reduces duplicate entry and improves confidence in daily task priorities.',
        'For agents, that means less admin friction and clearer next actions. For agency leadership, it means more reliable process quality across listings and better visibility into where support is needed. The objective is straightforward: improve efficiency in day-to-day delivery while maintaining consistent service standards from first contact to key handover.',
      ],
    },
    audienceFit: {
      heading: 'Who this page is for',
      paragraphs: [
        'Ideal for agencies that want an in-house office-style CRM with stronger workflow discipline across appraisal, campaign and sold-stage operations.',
      ],
      bullets: [
        'You want agents working from one daily workflow view, not multiple disconnected tools',
        'You need clearer handover from appraisal and campaign through to sold and key handover',
        'You are focused on improving team productivity and operational efficiency',
      ],
    },
    faqItems: [
      { question: 'What makes Workflow.Estate different from a generic CRM?', answer: 'Workflow.Estate is designed as an in-house office-style CRM that supports property workflows from market appraisal through to sold and key handover, not only lead pipeline tracking.' },
      { question: 'Can this improve daily productivity for agents?', answer: 'Yes. Workflow.Estate helps agents work from clearer daily task priorities and connected workflow context, reducing repetitive admin and missed follow-up steps.' },
      { question: 'Can agency admins use this as well as agents?', answer: 'Yes. The platform supports shared workflow visibility and coordination across agents, administrators and support staff.' },
      { question: 'Does it help with buyer follow-up consistency?', answer: 'Yes. Teams can define and track repeatable follow-up sequences linked to each property and open home activity.' },
    ],
    ctaHeading: 'Move from contact storage to workflow execution',
    ctaBody: 'Book a demo to see how Workflow.Estate can improve daily agent productivity and office efficiency from appraisal through to sold and handover.',
    ctaPrimaryText: 'Book a demo',
    ctaPrimaryTo: '/contact-sales',
    ctaSecondaryText: 'View features',
    ctaSecondaryTo: '/',
    internalLinks: sharedInternalLinks,
  },
  '/open-house-management-software': {
    slug: '/open-house-management-software',
    title: 'Open House Management Software | Workflow.Estate',
    description: 'Open house management software for Australian agencies. Capture attendees, track buyer follow-up and connect open homes to listing workflows.',
    h1: 'Open House Management Software for Real Estate Teams',
    heroSubtitle: 'Capture every open home attendee, organise follow-up and connect inspection activity to your broader listing workflow.',
    intro: 'Workflow.Estate helps agencies run open homes with less admin and better follow-through. Attendance, buyer intent and next actions stay visible so fewer quality leads are left behind.',
    keyBenefits: [
      'Digital attendee capture with immediate CRM visibility',
      'Structured post-open follow-up for agents and teams',
      'Buyer tracking linked to property campaigns',
      'Open home insights feeding directly into listing operations',
    ],
    problemSolution: {
      heading: 'Open homes create leads, but manual process loses momentum',
      paragraphs: [
        'Open homes can generate strong buyer interest, but manual sign-ins and ad hoc follow-up often cause delays and missed opportunities.',
        'Workflow.Estate gives agencies one connected open home process: capture attendees, qualify interest and trigger next actions quickly.',
      ],
      bullets: [
        'Capture visitor details in a structured format',
        'Record notes and buyer signals for better follow-up',
        'Push outcomes into property lifecycle workflow',
      ],
    },
    featureHighlights: [
      { title: 'Attendee capture', description: 'Track open home attendance and preserve clean lead records for follow-up.' },
      { title: 'Buyer follow-up workflows', description: 'Assign and complete next-step actions while interest is still high.' },
      { title: 'Property-linked activity', description: 'Connect open home outcomes with campaign and listing progress.' },
      { title: 'Team-wide visibility', description: 'Give sales and admin teams a shared picture of open home outcomes.' },
    ],
    whyWorkflowEstate: [
      'Designed to make open home execution and follow-up practical',
      'Connects attendance capture with broader agency workflows',
      'Supports consistent processes across different team members',
    ],
    deepDive: {
      heading: 'Open home management software should improve conversion, not just sign-in',
      paragraphs: [
        'Open homes are one of the most important lead-generation moments in residential sales, but the operational handover after inspections is where many agencies lose momentum. If attendance is captured without structured follow-up, buyer intent fades quickly and agents are forced to rebuild context from memory.',
        'Workflow.Estate treats open homes as part of an end-to-end workflow. Visitor details, notes and buyer signals are captured in a way that can be acted on immediately. Follow-up tasks become visible, ownership is clear, and property-level context stays attached to each buyer conversation.',
        'This matters for both agents and agency leadership. Agents can prioritise warm opportunities faster, while principals and admins can monitor whether inspection follow-up standards are being met. Over time, that creates a more disciplined operating rhythm around open homes and improves the consistency of buyer engagement.',
      ],
    },
    audienceFit: {
      heading: 'A fit for agencies focused on better open home conversion',
      paragraphs: [
        'If open home traffic is strong but follow-up is inconsistent, this workflow helps teams convert more interest into quality buyer conversations.',
      ],
    },
    faqItems: [
      { question: 'Can Workflow.Estate be used for weekend open homes?', answer: 'Yes. It supports attendee capture and follow-up workflows for regular open home activity.' },
      { question: 'Does it connect open homes to CRM records?', answer: 'Yes. Open home activity can be linked to buyer records and property context.' },
      { question: 'Is this software only for inspections?', answer: 'No. It also supports related listing workflow and agency operations around each property campaign.' },
    ],
    ctaHeading: 'Improve open home follow-up quality',
    ctaBody: 'See a practical demo of attendee capture, buyer tracking and post-open workflows tailored to your agency.',
    ctaPrimaryText: 'Book a demo',
    ctaPrimaryTo: '/contact-sales',
    ctaSecondaryText: 'See platform overview',
    ctaSecondaryTo: '/',
    internalLinks: sharedInternalLinks,
  },
  '/workflow-estate-vs-agentbox': {
    slug: '/workflow-estate-vs-agentbox',
    title: 'Workflow.Estate vs Agentbox | CRM Comparison',
    description: 'Compare Workflow.Estate vs Agentbox by workflow approach. See which CRM style better matches your agency operations and follow-up process.',
    h1: 'Workflow.Estate vs Agentbox: Workflow Approach Comparison',
    heroSubtitle: 'A professional comparison focused on workflow depth across sales and rentals for Australian real estate agencies.',
    intro: 'Every agency operates differently. This page outlines where Workflow.Estate is positioned with stronger workflow coverage: from marketing appraisal through to sold or leased, with operational visibility across agents, administrators, vendors, landlords, buyers and renters.',
    keyBenefits: [
      'Understand how Workflow.Estate manages the property lifecycle from appraisal to sold or leased',
      'Evaluate support for both sales and rental workflows, including configurable workflow design',
      'Compare operational depth across agreements, campaigns, lead flow and stakeholder coordination',
      'Use a practical evaluation framework before selecting a platform',
    ],
    problemSolution: {
      heading: 'How to compare Workflow.Estate vs Agentbox in practical terms',
      paragraphs: [
        'A useful comparison should begin with your operating model rather than a broad feature checklist. The core question is whether the platform supports the full cycle your agency runs in practice.',
        'Workflow.Estate is designed as a CRM and workflow operations platform, with emphasis on appraisal-to-campaign-to-transaction execution, lead tracking and team accountability.',
      ],
      bullets: [
        'Assess whether properties can be managed from marketing appraisal through to sold or leased status',
        'Confirm support for both sales and rental workflows, including standard templates and customised processes',
        'Verify how open scheduling, attendee capture and lead follow-up are connected operationally',
      ],
    },
    featureHighlights: [
      {
        title: 'End-to-end property lifecycle control',
        description: 'Workflow.Estate is designed to manage properties from marketing appraisal to sold or leased outcomes, with stage-level visibility throughout the campaign.',
      },
      {
        title: 'Sales + rental workflow support',
        description: 'Agencies can run both sales and rental operations using standard workflow structures, with custom workflow templates where required.',
      },
      {
        title: 'Appraisal and agreement workflow support',
        description: 'The platform supports market appraisal reporting and agency or rental agreement steps within the broader property process.',
      },
      {
        title: 'Integrated signature workflow',
        description: 'Signature is supported in document generation workflows, helping agencies complete agreements without separate DocuSign costs in supported flows.',
      },
      {
        title: 'AI-assisted listing content',
        description: 'Workflow.Estate supports AI-generated property description workflows to help teams produce listing content more efficiently.',
      },
      {
        title: 'Stakeholder coordination across the cycle',
        description: 'Agents, administrators, vendors or landlords, buyers or renters and contractors remain aligned through shared workflow context.',
      },
      {
        title: 'Open scheduling and lead tracking',
        description: 'Open scheduling, attendee capture and lead follow-up are connected so inspection activity moves directly into actionable CRM workflows.',
      },
      {
        title: 'Visitor self check-in and open-home tracking',
        description: 'Workflow.Estate includes self check-in support and attendee tracking to strengthen inspection-day data capture and post-open follow-up quality.',
      },
      {
        title: 'Templated market campaigns',
        description: 'Teams can use structured market campaign workflows to run repeatable outreach and follow-up aligned to listing activity.',
      },
      {
        title: 'Third-party listing integration',
        description: 'Workflow.Estate includes third-party listing posting workflow support, including realestate.com.au integration.',
      },
      {
        title: 'Portal integration coverage (REA + Domain)',
        description: 'The platform supports listing-portal integration workflows, including realestate.com.au and Domain connection and status management.',
      },
      {
        title: 'Vendor and agreement response workflows',
        description: 'Secure vendor report and agreement flows support review, response and signature capture during appraisal and campaign stages.',
      },
      {
        title: 'Contractor operations portal',
        description: 'A dedicated contractor workflow supports job progression, file exchange, status updates, invoice handling and availability management.',
      },
      {
        title: 'Commission and performance reporting',
        description: 'Workflow.Estate includes commission tracking and operational reporting to support agency-level performance visibility and decision-making.',
      },
    ],
    whyWorkflowEstate: [
      'Designed for agencies that require workflow depth, not only contact management',
      'Connects listing execution, lead flow, agreements and campaigns within one operating model',
      'Supports consistent coordination across the full sales or rental cycle',
    ],
    deepDive: {
      heading: 'Where Workflow.Estate is positioned to do more operationally',
      paragraphs: [
        'Workflow.Estate is intentionally built for agencies that require stronger operational control from appraisal through transaction completion. Rather than separating CRM and execution, it links property stages, lead progression, agreements, communications and team tasks in one workflow model.',
        'For agencies operating across both sales and rentals, this is particularly relevant. Workflow.Estate supports standard process structures while allowing customised workflows, enabling consistency without forcing identical operating patterns across every team. This includes open scheduling, lead handling, market campaign execution and third-party listing processes.',
        'From a positioning perspective, Workflow.Estate is designed for agencies seeking broader workflow coverage with disciplined execution. Pricing should always be assessed against plan requirements and operating scope, but the product direction is clear: support more of the end-to-end agency process within a single platform.',
      ],
      bullets: [
        'Property journey coverage from marketing appraisal to sold/leased',
        'Sales and rental support with standard and custom workflow options',
        'Signature support in generated document workflows without separate DocuSign cost',
        'AI-assisted property description, agreement workflows and campaign templates',
        'Integrated open scheduling, lead tracking, self check-in and stakeholder coordination',
        'Additional operational modules including contractor workflows, portal integrations and commission reporting',
      ],
    },
    audienceFit: {
      heading: 'When Workflow.Estate may be the better fit',
      paragraphs: [
        'Workflow.Estate is typically a strong fit where an agency requires deeper operational workflow support across sales and rentals, rather than contact management alone.',
      ],
      bullets: [
        'You want to run appraisal-to-sold/leased workflows with clear stage ownership',
        'You need open scheduling, attendee-to-lead tracking and follow-up in one flow',
        'You value templated campaigns, agreement workflows and third-party listing support',
        'You need vendor, contractor and internal team coordination within one platform',
        'You are evaluating a more cost-conscious platform option for your agency model',
      ],
    },
    faqItems: [
      { question: 'Is this page claiming exact feature parity with Agentbox?', answer: 'No. This page focuses on Workflow.Estate positioning and workflow priorities, not exact one-to-one feature parity claims.' },
      { question: 'Can Workflow.Estate handle both sales and rental operations?', answer: 'Yes. Workflow.Estate supports both sales and rental workflows, including standard structures and custom workflow configurations.' },
      { question: 'Does Workflow.Estate support third-party listing posting?', answer: 'Yes. Workflow.Estate includes third-party listing workflow support, including realestate.com.au integration.' },
      { question: 'Does Workflow.Estate also support Domain integration?', answer: 'Yes. Workflow.Estate includes integration workflows for both realestate.com.au and Domain, with in-platform connection and status handling.' },
      { question: 'Is signature included in document generation workflows?', answer: 'Workflow.Estate supports signature in document generation workflows, helping agencies complete key documents without separate DocuSign costs in supported flows.' },
      { question: 'Are contractor and commission workflows included?', answer: 'Workflow.Estate includes contractor job workflow support and commission reporting tools as part of its broader agency operations model.' },
      { question: 'Is Workflow.Estate always cheaper?', answer: 'Pricing depends on plan and agency requirements. The most reliable approach is to compare total value against your required workflows, users and integration needs.' },
    ],
    ctaHeading: 'Compare based on your real workflow',
    ctaBody: 'Book a tailored walkthrough and map your current operating process against Workflow.Estate to assess practical fit.',
    ctaPrimaryText: 'Book a comparison demo',
    ctaPrimaryTo: '/contact-sales',
    ctaSecondaryText: 'See product demo',
    ctaSecondaryTo: '/demo',
    internalLinks: sharedInternalLinks,
  },
};
